import React, {useState, useRef} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {useApp} from '../context/AppContext';

const {FiMenu, FiBook, FiBell, FiEdit3, FiCalendar, FiClock, FiUser, FiSettings, FiLogOut, FiCamera} = FiIcons;

const Header = ({onMenuClick, user, onLogout, onOpenProfile}) => {
  const {state, authService, dispatch} = useApp();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      // Convert image to base64 for storage
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageData = e.target.result;

        // Update profile with new image
        const updatedProfile = {
          ...state.profile,
          profile_picture: imageData
        };

        const {data, error} = await authService.updateProfile(state.user.id, updatedProfile);
        if (error) throw new Error(error);

        dispatch({type: 'SET_PROFILE', payload: data});
        alert('Profile picture updated successfully!');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      alert('Error uploading image: ' + error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  const getProfileImage = () => {
    if (state.profile?.profile_picture) {
      return state.profile.profile_picture;
    }
    return null;
  };

  const getInitials = () => {
    const name = state.profile?.name || user?.name || 'User';
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  return (
    <motion.header
      className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40"
      initial={{y: -50}}
      animate={{y: 0}}
      transition={{duration: 0.3}}
    >
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center space-x-4">
          {/* Always show menu button - removed lg:hidden */}
          <button
            onClick={onMenuClick}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <SafeIcon icon={FiMenu} className="w-6 h-6 text-gray-600" />
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 p-2 rounded-lg">
              <SafeIcon icon={FiBook} className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">CLO-PLO Mapper</h1>
              <p className="text-sm text-gray-500 font-medium">by FAHIM</p>
            </div>
          </div>
        </div>

        {/* Current Course Display - Enhanced */}
        <div className="flex items-center space-x-4">
          {state.activeCourse && (
            <div className="hidden md:flex items-center space-x-3 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
              <SafeIcon icon={FiEdit3} className="w-4 h-4 text-blue-600" />
              <div className="text-sm">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-blue-900">
                    {state.activeCourse.course_code || state.activeCourse.courseCode}
                  </span>
                  <span className="text-blue-600">•</span>
                  <span className="text-blue-800">
                    {state.activeCourse.course_name || state.activeCourse.courseName}
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-blue-600 text-xs mt-1">
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiCalendar} className="w-3 h-3" />
                    <span>{state.activeCourse.semester}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiClock} className="w-3 h-3" />
                    <span>{state.activeCourse.session}</span>
                  </div>
                  <span>•</span>
                  <span>{state.activeCourse.faculty}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-3">
            <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors relative">
              <SafeIcon icon={FiBell} className="w-5 h-5 text-gray-600" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
            </button>

            {/* Enhanced Profile Menu */}
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center space-x-2 p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="relative">
                  {getProfileImage() ? (
                    <img
                      src={getProfileImage()}
                      alt="Profile"
                      className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {getInitials()}
                      </span>
                    </div>
                  )}
                  {uploadingImage && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-gray-900">{state.profile?.name || user?.name}</p>
                  <p className="text-xs text-gray-500">{state.profile?.position || user?.position}</p>
                </div>
              </button>

              {/* Profile Dropdown Menu */}
              <AnimatePresence>
                {showProfileMenu && (
                  <motion.div
                    initial={{opacity: 0, scale: 0.95, y: -10}}
                    animate={{opacity: 1, scale: 1, y: 0}}
                    exit={{opacity: 0, scale: 0.95, y: -10}}
                    transition={{duration: 0.2}}
                    className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                  >
                    {/* Profile Header */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          {getProfileImage() ? (
                            <img
                              src={getProfileImage()}
                              alt="Profile"
                              className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                              <span className="text-white text-lg font-medium">
                                {getInitials()}
                              </span>
                            </div>
                          )}
                          <button
                            onClick={triggerImageUpload}
                            disabled={uploadingImage}
                            className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors disabled:opacity-50"
                            title="Change profile picture"
                          >
                            <SafeIcon icon={FiCamera} className="w-3 h-3 text-white" />
                          </button>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{state.profile?.name || user?.name}</h3>
                          <p className="text-sm text-gray-600">{state.profile?.email || user?.email}</p>
                          <p className="text-xs text-gray-500">{state.profile?.position || user?.position}</p>
                        </div>
                      </div>
                    </div>

                    {/* Profile Details */}
                    <div className="px-4 py-2 text-sm text-gray-600 border-b border-gray-100">
                      <div className="space-y-1">
                        {state.profile?.faculty && (
                          <p><span className="font-medium">Faculty:</span> {state.profile.faculty}</p>
                        )}
                        {state.profile?.department && (
                          <p><span className="font-medium">Department:</span> {state.profile.department}</p>
                        )}
                        {state.profile?.phone && (
                          <p><span className="font-medium">Phone:</span> {state.profile.phone}</p>
                        )}
                        {state.profile?.office && (
                          <p><span className="font-medium">Office:</span> {state.profile.office}</p>
                        )}
                      </div>
                    </div>

                    {/* Menu Actions */}
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowProfileMenu(false);
                          onOpenProfile();
                        }}
                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <SafeIcon icon={FiUser} className="w-4 h-4" />
                        <span>Edit Profile</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowProfileMenu(false);
                          // You can add settings functionality here
                        }}
                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <SafeIcon icon={FiSettings} className="w-4 h-4" />
                        <span>Settings</span>
                      </button>
                      <hr className="my-1" />
                      <button
                        onClick={() => {
                          setShowProfileMenu(false);
                          onLogout();
                        }}
                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Course Display - Enhanced */}
      {state.activeCourse && (
        <div className="md:hidden px-4 pb-3">
          <div className="bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-2 mb-1">
              <SafeIcon icon={FiEdit3} className="w-4 h-4 text-blue-600" />
              <div className="text-sm">
                <span className="font-medium text-blue-900">
                  {state.activeCourse.course_code || state.activeCourse.courseCode}
                </span>
                <span className="text-blue-600 mx-2">•</span>
                <span className="text-blue-800">
                  {state.activeCourse.course_name || state.activeCourse.courseName}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-4 text-blue-600 text-xs">
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiCalendar} className="w-3 h-3" />
                <span>{state.activeCourse.semester}</span>
              </div>
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiClock} className="w-3 h-3" />
                <span>{state.activeCourse.session}</span>
              </div>
              <span>•</span>
              <span className="truncate">{state.activeCourse.faculty}</span>
            </div>
          </div>
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />

      {/* Click Outside Handler */}
      {showProfileMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowProfileMenu(false)}
        />
      )}
    </motion.header>
  );
};

export default Header;